<template>
  <v-layout>
    <v-flex>
      <h1 class="headline">Pick Up/Drop Off</h1>
      <v-layout align-center pa-2>
        <v-switch
          hide-details
          label="Is Pickup"
          v-model="requirePickup"
          class="mr-3"
        ></v-switch>
        <v-switch
          hide-details
          label="Is Dropoff"
          v-model="requireDropoff"
        ></v-switch>

        <v-spacer></v-spacer>

        <v-text-field
          append-icon="mdi-magnify"
          label="Quick Filter..."
          single-line
          hide-details
          v-model="search"
          :outlined="outlinedInputs"
        ></v-text-field>

        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn
              v-on="on"
              fab
              dark
              color="primary"
              class="ml-2"
              @click="createItem"
            >
              <v-icon dark>mdi-plus</v-icon>
            </v-btn>
          </template>
          <span>Create New Pickup/Dropoff</span>
        </v-tooltip>
      </v-layout>

      <v-card>
        <v-data-table
          :headers="headers"
          :items="items"
          :loading="$apollo.loading"
          v-bind="pagingOptions"
          :search="search"
          item-key="id"
          fixed-header
          :height="gridHeight"
          @current-items="dataTableReady"
        >
          <template v-slot:item.isPickup="{ item }">
            <v-checkbox
              v-model="item.isPickup"
              primary
              hide-details
              class="pa-0 ma-0"
              :disabled="true"
            ></v-checkbox>
          </template>
          <template v-slot:item.isDropoff="{ item }">
            <v-checkbox
              v-model="item.isDropoff"
              primary
              hide-details
              class="pa-0 ma-0"
              :disabled="true"
            ></v-checkbox>
          </template>
          <template v-slot:item.actions="{ item }">
            <default-list-actions
              :item="item"
              :hide-view="true"
              @deleteItem="deleteItem($event)"
              @editItem="editItem($event)"
              ><template v-slot:extraButton>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      v-on="on"
                      icon
                      small
                      class="mx-0"
                      @click="editItemContents(item)"
                    >
                      <v-icon color="blue" small>mdi-camera</v-icon>
                    </v-btn>
                  </template>
                  <span>Department Contents</span>
                </v-tooltip>
              </template>
            </default-list-actions>
          </template>
        </v-data-table>
      </v-card>

      <pickup-edit-dialog
        :show="pickupEditorDialog"
        @close="pickupEditorDialog = false"
        v-model="selectedPickup"
        @input="onSavePickup"
      />
      <contents-dialog
        v-if="selectedPickup && selectedPickup.id"
        :show="showContentsEditor"
        owner-name="PICKUP"
        :owner-id="selectedPickup.id"
        @close="showContentsEditor = false"
      ></contents-dialog>
      <div v-if="debug">
        <code>{{ first }}</code>
      </div>
      <get-more-results
        :first="first"
        @limitChange="onLimitChange"
        :items="pickupPoints"
        :totalItems="totalItems"
        :loading="$apollo.loading"
        :loadMoreEnabled="loadMoreEnabled"
        @loadMore="loadMore('pickupPoints')"
      ></get-more-results>
    </v-flex>
  </v-layout>
</template>

<script>
import {
  ALL_PICKUP_POINTS_SEARCH_QUERY,
  PICKUP_CREATE_MUTATION,
  PICKUP_DELETE_MUTATION,
  PICKUP_UPDATE_MUTATION,
} from "../../../../constants/graphql";

import DefaultListActions from "../../shared/DefaultListActions";
import { ListMixin } from "../../../../mixins/ListMixin";
import GetMoreResults from "../../shared/GetMoreResults";
import PickupEditDialog from "./PickupEditDialog.vue";
import { removeGQLTypename } from "../../../../helpers";
import ContentsDialog from "../../shared/contents/ContentsDialog.vue";

export default {
  name: "PickupsList",
  mixins: [ListMixin],
  components: {
    DefaultListActions,
    GetMoreResults,
    PickupEditDialog,
    ContentsDialog,
  },
  data() {
    return {
      debug: false,
      search: "",
      pickupPoints: {},
      selectedPickup: null,
      pickupEditorDialog: false,
      requirePickup: false,
      requireDropoff: false,
      showContentsEditor: false,
      headers: [
        {
          text: "Name",

          value: "name",
          name: "name",
        },
        {
          text: "Zone/Address",

          value: "zone",
          name: "zone",
        },
        {
          text: "Pickup",

          value: "isPickup",
          name: "pickup",
        },
        {
          text: "Dropoff",

          value: "isDropoff",
          name: "dropoff",
        },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          name: "actions",
          align: "center",
        },
      ],
    };
  },
  computed: {
    gridHeight() {
      if (!this.fixedHeader) {
        return null;
      }
      return this.items && this.items.length >= 20
        ? this.windowHeight - 330
        : null;
    },
    items() {
      return this.pickupPoints && this.pickupPoints.edges
        ? this.pickupPoints.edges.map(removeGQLTypename)
        : [];
    },
    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
  },
  methods: {
    editItemContents(item) {
      console.log(item);
      this.selectedPickup = { ...item };
      this.showContentsEditor = true;
    },
    deleteItem(item) {
      this.$apollo
        .mutate({
          mutation: PICKUP_DELETE_MUTATION,
          variables: {
            pickupId: item.id,
          },
        })
        .then(this.removeGqlObjectHandler, this.removeGqlObjectErrorHandler);
    },
    removeGqlObjectHandler(result) {
      this.$store.dispatch("showAppSnackbar", { msg: "Pickup removed." });
      if (this.$apollo.queries.pickupPoints != null) {
        this.$apollo.queries.pickupPoints.refetch();
      }
    },
    removeGqlObjectErrorHandler(err) {
      this.$store.dispatch("showAppSnackbar", {
        msg: err.graphQLErrors[0].message,
        color: "error",
      });
    },
    editItem(item) {
      this.selectedPickup = { ...item };
      this.pickupEditorDialog = true;
    },
    createItem() {
      this.selectedPickup = {};
      this.pickupEditorDialog = true;
    },
    onSaved(context) {
      if (this.$apollo.queries.pickupPoints != null) {
        this.$apollo.queries.pickupPoints.refetch();
      }
      this.closeDialog();
      let msg = context == "edit" ? "Pickup Updated." : "Pickup Created.";
      this.$store.dispatch("showAppSnackbar", { msg });
    },
    closeDialog() {
      this.pickupEditorDialog = false;
      this.selectedPickup = {};
    },
    onSavePickup(payload) {
      this.pickupEditorDialog = false;
      const { id, ...input } = payload;
      const mutation = id ? PICKUP_UPDATE_MUTATION : PICKUP_CREATE_MUTATION;
      this.$apollo
        .mutate({
          mutation,
          variables: { id, input },
          refetchQueries: [
            {
              query: ALL_PICKUP_POINTS_SEARCH_QUERY,
            },
          ],
        })
        .then(() => {
          this.$store.dispatch("showAppSnackbar", {
            msg: "Pickup Point Saved.",
          });
        })
        .catch((err) => {
          const errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
          this.$store.dispatch("showAppSnackbar", {
            msg: errorMsg,
            color: "error",
          });
        });
    },
  },
  apollo: {
    pickupPoints: {
      query: ALL_PICKUP_POINTS_SEARCH_QUERY,
      variables() {
        const filter = {};
        if (this.requirePickup && !this.requireDropoff) {
          filter.isPickup = true;
        } else if (!this.requirePickup && this.requireDropoff) {
          filter.isDropoff = true;
        } else if (this.requirePickup && this.requireDropoff) {
          filter.isDropoff = true;
          filter.isPickup = true;
        }
        return { filter, first: this.first, after: this.after };
      },
      result({ data }) {
        this.totalItems = data.pickupPoints.totalCount;
      },
    },
  },
};
</script>

<style scoped></style>



// WEBPACK FOOTER //
// src/components/gt/admin/destinations/PickupsList.vue